import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import type { SiteDocument, Href } from "@zirro/schema";
import { DocumentRenderer, resolvePage, useViewportBreakpoint } from "@zirro/renderer";
import { apiFetch, API_BASE_URL } from "../../lib/api";
import { useStorefront } from "../../lib/store-api";
import { Spinner } from "../../components/ui/Spinner";
import { parseDocumentPayload } from "../pages";
import { AlertCircle, ArrowLeft } from "lucide-react";
import { HeaderActions } from "./store/Cart";
import { Catalog } from "./store/Catalog";
import { CheckoutPage } from "./store/CheckoutPage";
import { OrderPage } from "./store/OrderPage";
import { ProductPage } from "./store/ProductPage";
import { StorefrontProvider, hasCatalogSlot } from "./store/StorefrontContext";
import { renderStoreSlot } from "./store/slots";
import { WishlistPage } from "./store/WishlistPage";
import "./store/store.css";

export interface PublishedSiteResponse {
  id: string;
  tenantId?: string;
  name: string;
  slug: string;
  status: string;
  publishedVersionId?: string;
  versionNumber: number;
  schemaVersion: number;
  document: SiteDocument | string;
}

import {
  type StoreRoute,
  storeRouteFor,
  renderStoreRoute,
} from "./store/routes";

export function PublicSitePage() {
  const { slug, "*": subpath } = useParams<{ slug: string; "*": string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const breakpoint = useViewportBreakpoint();

  const [site, setSite] = useState<PublishedSiteResponse | null>(null);
  const [doc, setDoc] = useState<SiteDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Sites without a store answer 404 here; that simply means "not a store".
  const storefront = useStorefront(slug);

  useEffect(() => {
    let unmounted = false;
    async function fetchPublishedSite() {
      if (!slug) return;
      setLoading(true);
      setError(null);
      try {
        let data: PublishedSiteResponse;
        try {
          data = await apiFetch<PublishedSiteResponse>(`/s/${slug}`);
        } catch {
          // Direct fallback to /s/${slug} with JSON header
          const res = await fetch(`${API_BASE_URL.replace(/\/api\/v1$/, "")}/s/${encodeURIComponent(slug)}`, {
            headers: { Accept: "application/json" },
          });
          if (!res.ok) {
            throw new Error(`HTTP ${res.status}`);
          }
          data = (await res.json()) as PublishedSiteResponse;
        }

        if (unmounted) return;
        setSite(data);
        const parsed = parseDocumentPayload(data.document);
        if (parsed) {
          setDoc(parsed);
          document.title =
            parsed.meta?.title || data.name || "Published Website";
        } else {
          setError("Site document could not be rendered.");
        }
      } catch (err: unknown) {
        if (unmounted) return;
        console.error("Error loading published site:", err);
        setError("Website not found or has not been published yet.");
      } finally {
        if (!unmounted) setLoading(false);
      }
    }
    fetchPublishedSite();
    return () => {
      unmounted = true;
    };
  }, [slug]);

  // Wait for the store too, so product slots never flash placeholder content.
  if (loading || storefront.isPending) {
    return (
      <div
        className="loading-center"
        style={{ minHeight: "100vh", background: "var(--z-gray-50, #f8fafc)" }}
      >
        <Spinner size="lg" />
        <h2
          style={{
            marginTop: "16px",
            color: "var(--z-gray-700, #334155)",
            fontSize: "1.25rem",
            fontWeight: 600,
          }}
        >
          Loading Website…
        </h2>
      </div>
    );
  }

  if (error || !doc || !site) {
    return (
      <div
        className="loading-center"
        style={{
          minHeight: "100vh",
          textAlign: "center",
          padding: "32px",
          background: "#f8fafc",
        }}
      >
        <div
          style={{
            width: "64px",
            height: "64px",
            borderRadius: "50%",
            background: "#fee2e2",
            color: "#ef4444",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "16px",
          }}
        >
          <AlertCircle size={32} />
        </div>
        <h1
          style={{
            fontSize: "1.75rem",
            fontWeight: 700,
            color: "#0f172a",
            marginBottom: "8px",
          }}
        >
          Website Not Available
        </h1>
        <p
          className="text-muted"
          style={{ maxWidth: "480px", marginBottom: "24px", color: "#64748b" }}
        >
          {error ??
            "The website you are looking for does not exist or has not been published yet."}
        </p>
        <Link className="btn btn--primary btn--lg" to="/">
          <ArrowLeft size={18} />
          Return to Zirro Home
        </Link>
      </div>
    );
  }

  // Resolve active page by route subpath
  let currentSlug = "/";
  if (subpath) {
    currentSlug = subpath.startsWith("/") ? subpath : `/${subpath}`;
  } else if (location.pathname.startsWith(`/s/${slug}/`)) {
    currentSlug = location.pathname.slice(`/s/${slug}`.length);
  } else if (location.pathname.startsWith(`/site/${slug}/`)) {
    currentSlug = location.pathname.slice(`/site/${slug}`.length);
  }

  const basePath = location.pathname.startsWith(`/site/${slug}`)
    ? `/site/${slug}`
    : `/s/${slug}`;

  const handleNavigate = (href: Href) => {
    if (href.type === "page" && href.pageId) {
      const target = doc.pages.find((p) => p.id === href.pageId);
      if (target) {
        const targetPath =
          target.slug === "/" ? basePath : `${basePath}${target.slug}`;
        navigate(targetPath);
        window.scrollTo({ top: 0 });
      }
    } else if (href.type === "url" && href.url) {
      if (href.url === "/" || href.url === "") {
        navigate(basePath);
        window.scrollTo({ top: 0 });
      } else if (href.url.startsWith("/")) {
        const matching = doc.pages.find(
          (p) => p.slug === href.url || p.slug === href.url.replace(/\/$/, ""),
        );
        if (matching) {
          const targetPath =
            matching.slug === "/" ? basePath : `${basePath}${matching.slug}`;
          navigate(targetPath);
          window.scrollTo({ top: 0 });
        } else {
          navigate(`${basePath}${href.url}`);
          window.scrollTo({ top: 0 });
        }
      } else if (href.url.startsWith("#")) {
        const el = document.getElementById(href.url.slice(1));
        el?.scrollIntoView({ behavior: "smooth" });
      } else {
        window.open(href.url, href.target === "_blank" ? "_blank" : "_self");
      }
    }
  };

  const store = storefront.data;
  if (!store) {
    const page =
      doc.pages.find((p) => p.slug === currentSlug) ?? resolvePage(doc);
    return (
      <div className="published-site-wrapper" style={{ minHeight: "100vh" }}>
        <DocumentRenderer
          document={doc}
          pageId={page?.id}
          mode="public"
          breakpoint={breakpoint}
          onNavigate={handleNavigate}
        />
      </div>
    );
  }

  const storeRoute = storeRouteFor(currentSlug);
  const page = storeRoute
    ? undefined
    : (doc.pages.find((p) => p.slug === currentSlug) ?? resolvePage(doc));
  // A "/shop" page without a catalog slot still lists every product after its content.
  const appendCatalog = page?.slug === "/shop" && !hasCatalogSlot(page.nodes);

  return (
    <StorefrontProvider store={store} basePath={basePath}>
      <div className="published-site-wrapper" style={{ minHeight: "100vh" }}>
        <DocumentRenderer
          document={doc}
          pageId={page?.id}
          activePage={
            storeRoute
              ? { id: "store-page", slug: currentSlug, title: "", nodes: [] }
              : undefined
          }
          mode="public"
          breakpoint={breakpoint}
          onNavigate={handleNavigate}
          renderSlot={renderStoreSlot}
          headerActions={<HeaderActions />}
          pageAddon={
            storeRoute ? (
              renderStoreRoute(storeRoute)
            ) : appendCatalog ? (
              <div className="zs-page">
                <Catalog />
              </div>
            ) : undefined
          }
        />
      </div>
    </StorefrontProvider>
  );
}
